import { FC, useEffect, useState } from 'react';
import { Button, Divider, Tabs } from 'antd';
import { TbSettingsCog } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownOptionAvatar, SmallTag, SmallTooltip } from '@/shared';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';
import { SetStateMethod } from '@/types';

import { resolveSearchPreferences, SearchPreferences } from '../../preferences';
import {
    hasSearchTabDividerAfter,
    isSearchTabEnabled,
    SEARCH_HORIZONTAL_GUTTER,
    searchTabDefinitions,
} from '../../searchTabs';
import ManageSearchModal from './ManageSearchModal';
import { SearchTabCount, useSearchTabCounts } from './useSearchTabCounts';

interface SearchTabsProps {
    clearFilters: () => void;
    setInput: SetStateMethod<string>;
}

const SearchTabCountTag: FC<{ count?: SearchTabCount }> = ({ count }): JSX.Element | null => {
    if (!count || (!count.loading && count.value === undefined)) {
        return null;
    }

    const formattedCount = count.value?.toLocaleString();
    const displayCount = formattedCount ? `${formattedCount}${count.complete === false ? '+' : ''}` : undefined;

    return (
        <SmallTooltip
            title={
                formattedCount
                    ? `${count.complete === false ? 'At least ' : ''}${formattedCount} ${count.unit} available`
                    : `Loading ${count.unit}`
            }
        >
            <span style={{ alignItems: 'center', display: 'inline-flex' }}>
                <SmallTag compact loading={count.loading} message={displayCount} styles={{ marginLeft: 0 }} />
            </span>
        </SmallTooltip>
    );
};

const SearchTabs: FC<SearchTabsProps> = ({ clearFilters, setInput }): JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();

    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const { preferences } = useUserPreferences();
    const searchPreferences = resolveSearchPreferences(preferences?.search as SearchPreferences | undefined);
    const visibleTabs = searchTabDefinitions.filter((tab) => isSearchTabEnabled(tab, searchPreferences.tabs));
    const tabCounts = useSearchTabCounts();

    const tabsActiveKey = location.pathname.split('/')[2];
    const isActiveTabVisible = visibleTabs.some((tab) => tab.key === tabsActiveKey);

    const { token } = useAntDesignTheme();

    useEffect(() => {
        if (tabsActiveKey && !isActiveTabVisible) {
            navigate(`/search/${searchPreferences.defaultTab}`, { replace: true });
        }
    }, [isActiveTabVisible, navigate, searchPreferences.defaultTab, tabsActiveKey]);

    return (
        <>
            <Tabs
                tabBarStyle={{ padding: `0 ${SEARCH_HORIZONTAL_GUTTER}`, marginBottom: 0 }}
                size='small'
                activeKey={tabsActiveKey}
                onChange={(activeKey: string) => {
                    navigate(`/search/${activeKey}`);

                    setInput('');

                    if (!searchPreferences.filters.preserveOnTabChange) {
                        clearFilters();
                    }
                }}
                items={visibleTabs.map((tab) => ({
                    key: tab.key,
                    label: (
                        <span
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                gap: '0.375rem',
                                height: '100%',
                                lineHeight: 1,
                            }}
                        >
                            <DropdownOptionAvatar
                                icon={<tab.Icon size='0.75rem' />}
                                props={{ size: 20 }}
                                style={{ flex: 'none' }}
                            />
                            <span style={{ display: 'inline-flex', lineHeight: '1.25rem' }}>{tab.label}</span>
                            <SearchTabCountTag count={tabCounts[tab.key]} />
                            {hasSearchTabDividerAfter(tab.key) && (
                                <Divider
                                    type='vertical'
                                    style={{ borderColor: token.colorBorder, margin: '0 0 0 0.375rem' }}
                                />
                            )}
                        </span>
                    ),
                }))}
                tabBarExtraContent={
                    <SmallTooltip placement='left' title='Manage Search'>
                        <Button
                            aria-label='Manage Search'
                            onClick={() => setPreferencesOpen(true)}
                            size='small'
                            type='dashed'
                        >
                            <TbSettingsCog style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }} />
                        </Button>
                    </SmallTooltip>
                }
            />
            <ManageSearchModal open={preferencesOpen} setOpen={setPreferencesOpen} />
        </>
    );
};

export default SearchTabs;
